import FootTrafficFilterType from '@/Enums/Attribution/FootTraffic/FilterType.enum';
import FootTrafficReportType from '@/Enums/Attribution/FootTraffic/ReportType.enum';
import { IFootTrafficCampaignApiFilter } from '@/Interfaces/Attribution/ApiFilters.interface';
import { IFootTrafficFilterQuery, IFootTrafficReportPayload } from '@/Interfaces/Attribution/FootTraffic.interface';
import { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { RootState } from '@/store';
import { fetchFootTrafficFilterOptions, fetchFootTrafficReports } from '@/store/api/attribution.api';
import {
  selectFootTrafficClients,
  selectFootTrafficFilters,
  selectFootTrafficWhitelabels,
} from '@/store/selectors/attribution/footTraffic.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import { getControlOptionsValues } from '../filters/filterMapping';
import { getFootTrafficReportFilter, groupIdFilter } from './filters';

export const footTrafficRequestHandler =
  <T>(reportType: FootTrafficReportType, fallbackValues: T): AsyncThunkPayloadCreator<T, IAbortSignalThunkArg> =>
  async (_payload, { getState }) => {
    const state = getState() as RootState;

    const role = selectMeRole(state);
    const filters = selectFootTrafficFilters(state);

    const clients = selectFootTrafficClients(state);
    const whitelabels = selectFootTrafficWhitelabels(state);

    const appliedFilter = getFootTrafficReportFilter(filters, role, clients, whitelabels);
    if (!appliedFilter.advertiserIds?.length && !filters.campaigns?.length) {
      return fallbackValues;
    }

    const payload: IFootTrafficReportPayload = { filters: appliedFilter, reportType };

    return await fetchFootTrafficReports<T>(payload);
  };

export const fulfilledFootTrafficCaseHandler = (variableName: string) => (state: any, action: any) => {
  const requestIdVariableName = `${variableName}RequestId`;
  if (state[requestIdVariableName] === action.meta.requestId) {
    state[requestIdVariableName] = null;
    Object.entries(action.payload).forEach(([key, report]) => {
      if (Object.hasOwn(state, key)) {
        state[key] = report;
      }
    });
  }
};

export const footTrafficFilterRequestHandler =
  <T, R>(
    filterType: FootTrafficFilterType,
    fallbackValues: R,
    processResponseData: (responseData: T) => R,
  ): AsyncThunkPayloadCreator<R, IAbortSignalThunkArg> =>
  async (_payload, { getState }) => {
    const state = getState() as RootState;

    const filters = selectFootTrafficFilters(state);

    const advertiserIds = getControlOptionsValues(filters.advertisers);
    const campaignIds = groupIdFilter<IFootTrafficCampaignApiFilter>(filters.campaigns);

    if (!advertiserIds?.length) {
      return fallbackValues;
    }

    const params: IFootTrafficFilterQuery = { filterType, advertiserIds, campaignIds };

    const responseData = await fetchFootTrafficFilterOptions<T>(params);

    try {
      return processResponseData(responseData);
    } catch {
      return fallbackValues;
    }
  };
